import { useEffect, useState } from "react";
import { CalendarIcon } from "lucide-react";
import { format, startOfDay } from "date-fns";
import type { DateRange } from "react-day-picker";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { cn } from "../../utils/utils";

interface DateRangePickerProps {
  dateRange: DateRange | undefined;
  onDateRangeChange: (range: DateRange | undefined) => void;
  className?: string;
}

export function DateRangePicker({
  dateRange,
  onDateRangeChange,
  className,
}: DateRangePickerProps) {
  const [currentRange, setCurrentRange] = useState<DateRange | undefined>(dateRange);
  const [_isSelecting, setIsSelecting] = useState<boolean>(!dateRange?.to);

  useEffect(() => {
    setCurrentRange(dateRange);
  }, [dateRange]);

  // Normalize dates to avoid time component issues
  const normalizeDate = (date: Date) => startOfDay(date);

  const handleSelect = (selectedDate?: Date) => {
    if (!selectedDate) return;

    const normalized = normalizeDate(selectedDate);

    // If a full range already exists, restart with new start
    if (currentRange?.from && currentRange?.to) {
      const newRange = { from: normalized, to: undefined };
      setCurrentRange(newRange);
      setIsSelecting(true);
      onDateRangeChange(newRange);
      return;
    }

    // If we're selecting the end date
    if (currentRange?.from && !currentRange?.to) {
      if (normalized.getTime() < currentRange.from.getTime()) {
        // If end date is before start, flip them
        const newRange = { from: normalized, to: currentRange.from };
        setCurrentRange(newRange);
        setIsSelecting(false);
        onDateRangeChange(newRange);
      } else {
        const newRange = { from: currentRange.from, to: normalized };
        setCurrentRange(newRange);
        setIsSelecting(false);
        onDateRangeChange(newRange);
      }
      return;
    }

    // First click — set start date
    const newRange = { from: normalized, to: undefined };
    setCurrentRange(newRange);
    setIsSelecting(true);
    onDateRangeChange(newRange);
  };



  const modifiers = {
    start: currentRange?.from,
    end: currentRange?.to,
    inRange:
      currentRange?.from && currentRange?.to
        ? {
          from: currentRange.from,
          to: currentRange.to,
        }
        : undefined,
  };

  const modifiersClassNames = {
    start: "bg-blue-500 text-sm font-bold rounded-l-md z-10",
    end: "bg-blue-500 text-sm font-bold rounded-r-md z-10",
    inRange: "bg-blue-500/20 text-sm",
    outside: "bg-transparent text-muted", // make sure outside days are styled plainly
  };


  return (
    <div className={cn("grid gap-2 font-semibold mb-1", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant="default"
            size="sm"
            className={cn("justify-start text-left", !currentRange?.from && "text-muted-foreground")}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {currentRange?.from ? (
              currentRange.to ? (
                <>
                  {format(currentRange.from, "LLL dd, y")} – {format(currentRange.to, "LLL dd, y")}

                </>
              ) : (
                format(currentRange?.from, "LLL dd, y")
              )
            ) : (
              <span>Pick a date range</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-2" align="start">
          <p className="text-sm text-gray-500 px-2 pb-1">
            {currentRange?.from && !currentRange?.to
              ? "Select the end date"
              : "Select a start date"
            }
          </p>
          <Calendar
            className="custom-datepicker flex gap-4 justify-between [&_.rdp-month]:w-[280px] [&_.rdp-caption]:w-[140px] [&_.rdp-caption]:text-center"
            autoFocus
            mode="range"
            numberOfMonths={2}
            selected={currentRange}
            defaultMonth={currentRange?.from ?? new Date()}
            onDayClick={handleSelect}
            showOutsideDays={false}
            modifiers={modifiers}
            modifiersClassNames={modifiersClassNames}
            modifiersStyles={{
              outside: {
                backgroundColor: "transparent",
                color: "transparent",
                pointerEvents: "none", // optional: makes them unclickable
              },
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}